import { useMemo } from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';
import { UserInfo } from '../common/model/dto/user-info';
import {
  SnapshotSource,
  SnapshotTile,
} from '../common/model/type/clinical-snapshot.type';
import { ClinicalSnapshotDataUtils } from '../common/utils/services/clinical-snapshot-data-utils';
import {
  clinicLocationClinicalDataQueryKeys,
  getPatientCycles,
  getPatientReproductiveStage,
} from '../services/clinic-location-clinical-data.service';

const ALL_CYCLES_OLDEST_FIRST = queryString.stringify({
  limit: 0,
  order_by: 'start_day',
  order_dir: 'asc',
});

function toSnapshotSource<T>(
  query: UseQueryResult<T>,
  isAwaitingInputs: boolean
): SnapshotSource<T> {
  return {
    data: query.data,
    isLoading: isAwaitingInputs || query.isLoading,
    isError: query.isError,
  };
}

export function useClinicalSnapshot(
  clinicLocationId: string | undefined,
  patient: UserInfo | undefined
): { tiles: SnapshotTile[] } {
  const { i18n } = useTranslation();
  const patientId = patient?.id;
  const hasPatientContext = !!clinicLocationId && !!patientId;
  const isStagingApplicable =
    ClinicalSnapshotDataUtils.isStagingApplicable(patient);

  const reproductiveStage = useQuery({
    queryKey: [
      'clinic-location-patient-reproductive-stage',
      clinicLocationId,
      patientId,
    ],
    queryFn: () => getPatientReproductiveStage(clinicLocationId!, patientId!),
    enabled: hasPatientContext && isStagingApplicable,
  });

  const cycles = useQuery({
    queryKey: clinicLocationClinicalDataQueryKeys.cycles(
      clinicLocationId,
      patientId,
      ALL_CYCLES_OLDEST_FIRST
    ),
    queryFn: () =>
      getPatientCycles(clinicLocationId!, patientId!, ALL_CYCLES_OLDEST_FIRST),
    enabled: hasPatientContext,
  });

  const tiles = useMemo(
    () =>
      ClinicalSnapshotDataUtils.mapToSnapshotTiles(
        patient,
        toSnapshotSource(
          reproductiveStage,
          isStagingApplicable && !hasPatientContext
        ),
        toSnapshotSource(cycles, !hasPatientContext)
      ),
    // The mapping translates its own copy, so a language change has to recompute it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      patient,
      reproductiveStage.data,
      reproductiveStage.isLoading,
      reproductiveStage.isError,
      cycles.data,
      cycles.isLoading,
      cycles.isError,
      hasPatientContext,
      isStagingApplicable,
      i18n.language,
    ]
  );

  return { tiles };
}
